import type { TreePatch, PatchQueueEntry } from "./Types.ts";

// ─── PatchQueue ───────────────────────────────────────────────────────────────
//
// Tracks every patch the client has sent but not yet received a server response
// for. Patches are keyed by their UUID clientId.
//
// Responsibilities:
//   - enqueue: record a sent patch with its send timestamp
//   - acknowledge: remove a patch when the server responds (success or rejection)
//   - getTimedOutEntries: find patches that have exceeded a retry timeout
//   - recordRetry: reset the timeout window after a retry is sent
//   - removeForSubtree: drop all pending patches for a node subtree when an
//     "add" patch for the parent is rejected by the server

export class PatchQueue {
    // Insertion-ordered map so getAllPending returns patches in send order.
    private readonly pending: Map<string, PatchQueueEntry>;

    constructor() {
        this.pending = new Map();
    }

    // ─── Core operations ──────────────────────────────────────────────────────

    /**
     * Records a patch as pending. Called immediately before sending to the server.
     */
    public enqueue(patch: TreePatch): void {
        const entry: PatchQueueEntry = {
            clientId: patch.clientId,
            patch,
            enqueuedAt: Date.now(),
            retryCount: 0,
        };
        this.pending.set(patch.clientId, entry);
    }

    /**
     * Removes the entry for the given clientId and returns it.
     * Called when the server responds — success or rejection.
     * Returns undefined if the clientId is not found (duplicate ack — safe to ignore).
     */
    public acknowledge(clientId: string): PatchQueueEntry | undefined {
        const entry = this.pending.get(clientId);
        if (entry) {
            this.pending.delete(clientId);
        }
        return entry;
    }

    /**
     * Returns all entries whose send time is older than timeoutMs milliseconds.
     * These patches should be resent with the same clientId (idempotent on server).
     */
 

    /**
     * Removes all pending patches whose nodeId is present in the given set.
     * Called when an "add" patch is rejected by the server: any child patches
     * that were queued against the now-rejected node are also invalid.
     */
    public removeForSubtree(nodeIds: Set<string>): void {
        for (const [clientId, entry] of this.pending.entries()) {
            if (nodeIds.has(entry.patch.nodeId)) {
                this.pending.delete(clientId);
            }
        }
    }

    // ─── Inspection ───────────────────────────────────────────────────────────

    public get(clientId: string): PatchQueueEntry | undefined {
        return this.pending.get(clientId);
    }

    public getAllPending(): PatchQueueEntry[] {
        return Array.from(this.pending.values());
    }

    public hasPending(): boolean {
        return this.pending.size > 0;
    }

    public pendingCount(): number {
        return this.pending.size;
    }

    /**
     * Removes all pending entries.
     * Called when a snapshot is loaded — in-flight patches are invalid against
     * the new tree state.
     */
    public clear(): void {
        this.pending.clear();
    }



       public getTimedOutEntries(timeoutMs: number): PatchQueueEntry[] {
        const now = Date.now();
        const timedOut: PatchQueueEntry[] = [];
        for (const entry of this.pending.values()) {
            if (now - entry.enqueuedAt > timeoutMs) {
                timedOut.push(entry);
            }
        }
        return timedOut;
    }

    /**
     * Increments the retry count and resets the timeout window for the given patch.
     * Call after the patch has been resent so the next timeout window starts fresh.
     */
    public recordRetry(clientId: string): void {
        const entry = this.pending.get(clientId);
        if (entry) {
            entry.retryCount += 1;
            entry.enqueuedAt = Date.now();
        }
    }
}
